import WarningImage from "@3gaam/shared/assets/images/warning-circle.svg";
import Icon from "@3gaam/shared/components/general/icon";
import Modal from "@3gaam/shared/components/general/modal";
import Image from "@3gaam/shared/components/image";
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import BToast from "react-bootstrap/Toast";

type PopoverData =
  | {
      type: "toast";
      title: string;
      description?: string;
      duration?: number;
      mode: "info" | "alert" | "done";
    }
  | {
      type: "modal";
      title: string;
      description?: string;
      onSubmit?: () => void;
      onDismiss?: () => void;
      icon?: React.ReactNode;
    }
  | {
      type: "minimalToast";
      title: string;
      description?: string;
      duration?: number;
      icon?: React.ReactNode;
    };

type ContextType = {
  show: boolean;
  data: PopoverData | undefined;
  open: (data: PopoverData) => void;
  hide: () => void;
};

const DEFAULT_DURATION = 5000;

let initialContext: ContextType = {
  show: false,
  data: undefined,
  open: () => {},
  hide: () => {},
};

let Context = createContext<ContextType>(initialContext);

export const Provider = ({ children }: { children: React.ReactNode }) => {
  let [show, setShow] = useState(false);
  let [data, setData] = useState<PopoverData | undefined>(undefined);
  let open = useCallback((data: PopoverData) => {
    setData(data);
    setShow(true);
  }, []);

  let hide = useCallback(() => {
    setData(undefined);
    setShow(false);
  }, []);

  let value = useMemo(
    () => ({
      show,
      data,
      open,
      hide,
    }),
    [data, hide, open, show],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const usePopover = () => {
  return useContext<ContextType>(Context);
};

export const Toast = () => {
  let { data, show, hide } = usePopover();
  return (
    <BToast
      show={show && data && data.type === "toast"}
      onClose={() => {
        hide();
      }}
      animation={true}
      autohide
      delay={
        data?.type === "toast"
          ? data?.duration || DEFAULT_DURATION
          : DEFAULT_DURATION
      }
    >
      <BToast.Body>
        {!!data && (
          <div
            className={`toast-wrapper ${
              data.type === "toast" ? data.mode : ""
            }`}
          >
            <div>
              <Icon name="far-fa-info-circle" />
            </div>
            <div>
              <h3>{data?.title}</h3>
              <p>{data?.description}</p>
            </div>
          </div>
        )}
      </BToast.Body>
    </BToast>
  );
};

export const MinimalToast = () => {
  let { data, show, hide } = usePopover();
  return (
    <BToast
      className="position-absolute"
      show={show && data && data.type === "minimalToast"}
      onClose={() => {
        hide();
      }}
      animation={true}
      autohide
      delay={
        data?.type === "minimalToast"
          ? data?.duration || DEFAULT_DURATION
          : DEFAULT_DURATION
      }
    >
      <BToast.Body>
        {!!data && (
          <div className={`toast-wrapper-minimal`}>
            <div className="d-flex h-100 align-items-center flex-row">
              {data.type === "minimalToast" && data.icon ? (
                data.icon
              ) : (
                <Icon name="far-fa-info-circle" />
              )}
            </div>
            <div>
              <span>{data?.title}</span>
              {!!data?.description && <p>{data?.description}</p>}
            </div>
          </div>
        )}
      </BToast.Body>
    </BToast>
  );
};

export const PModal = () => {
  let { data, show, hide } = usePopover();
  let onClose = useCallback(() => {
    if (data?.type !== "modal") return;
    hide();
    if (data.onDismiss) {
      data.onDismiss();
    }
  }, [data, hide]);
  let onSubmit = useCallback(() => {
    if (data?.type !== "modal") return;
    if (data.onSubmit) {
      data.onSubmit();
    }
  }, [data]);
  if (!data || data.type !== "modal") return null;
  return (
    <Modal
      setShow={onClose}
      showState={show}
      bodyClassName="py-4 px-3 px-lg-4 pos-rel text-center"
      noCloseBtn={true}
    >
      <>
        <div className="pt-3">
          <div className="mx-auto mb-4 w-60 pb-2">
            {data.icon ? data.icon : <Image src={WarningImage} />}
          </div>
          <p className="font-18 font-weight-semibold mb-2">{data.title} </p>
          <p className="text-light-gray mb-3">{data.description}</p>
        </div>
        <div className="d-flex justify-content-center mt-4 pt-2">
          <button onClick={onSubmit} className="btn btn-primary mx-1 py-2">
            باشه
          </button>
        </div>
      </>
    </Modal>
  );
};
